import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import { requestPasswordReset, submitNewPassword } from '../../../api/endpoints/profile';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';

const schema = z
  .object({
    token: z.string().min(1, 'E-postadaki bağlantıdan gelen kod gerekli'),
    newPassword: z.string().min(6, 'Parola en az 6 karakter olmalı'),
    confirmPassword: z.string().min(1, 'Parola tekrarı gerekli'),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: 'Parolalar eşleşmiyor',
    path: ['confirmPassword'],
  });

type FormValues = z.infer<typeof schema>;

/** Accept either the raw token or the whole `…/forgot-password?token=…` link pasted in. */
function extractToken(input: string): string {
  const trimmed = input.trim();
  const match = /[?&]token=([^&\s]+)/.exec(trimmed);
  return match ? decodeURIComponent(match[1]) : trimmed;
}

/**
 * Authenticated "Parola Değiştir" — the live API has no current-password change route, so
 * this reuses the password-reset flow (see api/endpoints/auth.ts): request an e-mail link,
 * then paste the code/link from it plus the new password. Same three Session/* routes as
 * the logged-out auth/ForgotPasswordPage.
 */
export function PasswordChangeCard({ username }: { username?: string }) {
  const { enqueueSnackbar } = useSnackbar();
  const [emailSent, setEmailSent] = useState(false);

  const sendMutation = useMutation({
    mutationFn: () => requestPasswordReset(username ?? ''),
    onSuccess: () => {
      setEmailSent(true);
      enqueueSnackbar('Sıfırlama bağlantısı e-posta adresinize gönderildi.', { variant: 'success' });
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { token: '', newPassword: '', confirmPassword: '' },
  });

  const resetMutation = useMutation({
    mutationFn: (values: FormValues) =>
      submitNewPassword({
        token: extractToken(values.token),
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      }),
    onSuccess: () => {
      enqueueSnackbar('Parolanız güncellendi.', { variant: 'success' });
      reset();
      setEmailSent(false);
    },
  });

  const submit = handleSubmit((values) => resetMutation.mutate(values));

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
          <LockResetIcon color="action" />
          <Typography variant="h6">Parola Değiştir</Typography>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Parolanızı değiştirmek için önce e-posta adresinize bir sıfırlama bağlantısı gönderin, ardından
          bağlantıdaki kodu ve yeni parolanızı aşağıya girin.
        </Typography>

        <Stack spacing={2}>
          <Box>
            <Button
              variant="outlined"
              disabled={sendMutation.isPending || !username}
              onClick={() => sendMutation.mutate()}
            >
              {sendMutation.isPending ? 'Gönderiliyor…' : 'Sıfırlama Bağlantısı Gönder'}
            </Button>
            <ApiErrorAlert error={sendMutation.error} sx={{ mt: 1 }} />
            {emailSent && !sendMutation.isPending && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Bağlantı gönderildi. Gelen kutunuzu kontrol edip e-postadaki kodu (veya bağlantının tamamını)
                aşağıya yapıştırın.
              </Alert>
            )}
          </Box>

          <Box component="form" onSubmit={submit}>
            <Stack spacing={2}>
              <TextField
                size="small"
                label="E-postadaki kod veya bağlantı"
                error={!!errors.token}
                helperText={errors.token?.message}
                {...register('token')}
              />
              <TextField
                size="small"
                type="password"
                label="Yeni Parola"
                error={!!errors.newPassword}
                helperText={errors.newPassword?.message}
                {...register('newPassword')}
              />
              <TextField
                size="small"
                type="password"
                label="Yeni Parola (Tekrar)"
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
                {...register('confirmPassword')}
              />
              <ApiErrorAlert error={resetMutation.error} />
              <Box>
                <Button type="submit" variant="contained" disabled={resetMutation.isPending}>
                  {resetMutation.isPending ? 'Kaydediliyor…' : 'Parolayı Güncelle'}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
